// Portal sessions — the session endpoints (backend/app/routes/portal_sessions.py),
// in one place. Contract: docs/portal-sessions.md.
//
// Every field the panel reads is named here and nowhere else. Nothing this module
// sends or reads is kept: an input event goes straight out, a frame goes straight
// to the caller's canvas. The bearer the server uses to talk to the task never
// reaches the browser; these calls carry only the Atlas session (Clerk token /
// cookie) that `authFetch.js` attaches to every /api/* fetch.
//
// FRAMES ARE A FETCH, NOT AN EventSource, and that is not a preference: Atlas
// authenticates /api/* with the Clerk session token the fetch wrapper attaches as
// a header, and EventSource cannot carry a header. So `openPortalFrames` reads the
// Server-Sent Events body with a stream reader and parses the events itself — the
// same wire format, the same `data:` lines, no library.

import { post, request } from "./api";

const API_URL = process.env.REACT_APP_API_URL || "";
const base = (practiceId) => `${API_URL}/api/practices/${practiceId}/portal-session`;

function num(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function normalizeSession(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ?? null,
    portalKey: raw.portal_key || null,
    status: raw.status || null,
    startedBy: raw.started_by ?? null,
    startedByEmail: raw.started_by_email || null,
    startedAt: raw.started_at || null,
    lastSeenAt: raw.last_seen_at || null,
    endedAt: raw.ended_at || null,
    taskId: raw.task_id || null,
    reasonEnded: raw.reason_ended || null,
  };
}

// The task's /state, reduced to what the strip shows. `signedIn` is the task's
// GUESS (true / false / null = it cannot tell). The URL is kept only as its host:
// a page title or a path can name a patient, and the strip is not the place.
export function normalizeState(raw) {
  const s = raw || {};
  let host = null;
  if (typeof s.url === "string") {
    try {
      host = new URL(s.url).host || null;
    } catch (err) {
      host = null;
    }
  }
  return {
    signedIn: s.signed_in === true ? true : s.signed_in === false ? false : null,
    idleSeconds: num(s.idle_seconds),
    host,
  };
}

// GET …/portal-session → {enabled, portals, session}
export async function fetchPortalSession(practiceId) {
  const body = await request(base(practiceId), {}, "Could not load the portal session.");
  return {
    enabled: body.enabled === true,
    portals: Array.isArray(body.portals)
      ? body.portals
          .filter((p) => p && typeof p.key === "string")
          .map((p) => ({ key: p.key, label: p.label || p.key }))
      : [],
    session: normalizeSession(body.session),
  };
}

// POST …/start {portal} → 201 {session}; 409 while one is live; 503 when off.
export async function startPortalSession(practiceId, portal) {
  const body = await request(
    `${base(practiceId)}/start`,
    post({ portal }),
    "Could not start the portal session.",
  );
  return normalizeSession(body.session);
}

// POST …/end → {session}
export async function endPortalSession(practiceId) {
  const body = await request(`${base(practiceId)}/end`, post({}), "Could not end the portal session.");
  return normalizeSession(body.session);
}

// GET …/state → {session, state}
export async function fetchPortalSessionState(practiceId) {
  const body = await request(`${base(practiceId)}/state`, {}, "Could not read the session state.");
  return { session: normalizeSession(body.session), state: normalizeState(body.state) };
}

// POST …/input — one event, forwarded as given. The caller builds the event from
// key names and codes, a character, or coordinates (the key contract is in
// docs/portal-sessions.md); nothing about it is retained here.
export function sendPortalInput(practiceId, event) {
  return request(`${base(practiceId)}/input`, post(event), "The input could not be sent.");
}

// POST …/navigate {url}
export function navigatePortal(practiceId, url) {
  return request(`${base(practiceId)}/navigate`, post({ url }), "Could not navigate the session.");
}

// One SSE event's data, as the task sends it: the base64 JPEG itself, or a JSON
// object carrying it. Returns the base64 string or null.
export function frameFromEvent(data) {
  if (typeof data !== "string" || !data) return null;
  const trimmed = data.trim();
  if (trimmed.startsWith("{")) {
    try {
      const obj = JSON.parse(trimmed);
      const b64 = obj.jpeg || obj.frame || obj.data || obj.image;
      return typeof b64 === "string" && b64 ? b64 : null;
    } catch (err) {
      return null;
    }
  }
  return trimmed;
}

// Parse SSE text incrementally. `buffer` is whatever was left from the last call;
// returns {events: [{event, data}], rest}. Comment lines (keepalives) are dropped.
export function parseSse(buffer) {
  const events = [];
  let rest = buffer;
  let idx;
  while ((idx = rest.indexOf("\n\n")) !== -1) {
    const block = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    let event = "message";
    const data = [];
    for (const line of block.split("\n")) {
      if (!line || line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "event") event = value;
      else if (field === "data") data.push(value);
    }
    if (data.length) events.push({ event, data: data.join("\n") });
  }
  return { events, rest };
}

// GET …/frames, streamed. Calls onFrame(base64Jpeg) per frame, onClose() when the
// stream ends for any reason other than stop(), onError(message) on a refusal.
// Returns stop(), which aborts the request; the server closes its upstream when
// this request goes away.
export function openPortalFrames(practiceId, { onFrame, onClose, onError }) {
  const controller = new AbortController();
  let stopped = false;

  (async () => {
    let response;
    try {
      response = await fetch(`${base(practiceId)}/frames`, {
        credentials: "include",
        signal: controller.signal,
        headers: { Accept: "text/event-stream" },
      });
    } catch (err) {
      if (!stopped && onClose) onClose();
      return;
    }
    if (!response.ok) {
      let message = "The frame stream could not be opened.";
      try {
        const body = await response.json();
        if (body && body.error) message = body.error;
      } catch (err) {
        /* not JSON */
      }
      if (!stopped && onError) onError(message, response.status);
      return;
    }
    if (!response.body || !response.body.getReader) {
      if (!stopped && onError) onError("This browser cannot read the frame stream.", 0);
      return;
    }
    const reader = response.body.getReader();
    let decoder = null;
    let buffer = "";
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        decoder = decoder || new TextDecoder();
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSse(buffer);
        buffer = parsed.rest;
        for (const ev of parsed.events) {
          const frame = frameFromEvent(ev.data);
          if (frame && onFrame) onFrame(frame);
        }
      }
    } catch (err) {
      /* aborted or the connection dropped: fall through to onClose */
    }
    if (!stopped && onClose) onClose();
  })();

  return function stop() {
    stopped = true;
    controller.abort();
  };
}

// ── the recorder ───────────────────────────────────────────────────────────
//
// POST …/recorder {mode} and GET …/recorder run atlas-billing-ops's recorder inside
// the signed-in session (super_admin only). The server stores the draft it produces
// as the next runbook version and the catalogue beside it; NEITHER IS EVER IN THE
// RESPONSE, and this normaliser keeps only the run's status, counts, log tail and
// the version number, so nothing of a draft can land in React state.

export const RECORDER_MODES = ["scan", "crawl"];

function countsOf(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "number" && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

export function normalizeRecorderRun(raw) {
  if (!raw || typeof raw !== "object") return null;
  const state = ["running", "done", "failed"].includes(raw.state) ? raw.state : "running";
  return {
    runId: typeof raw.run_id === "string" ? raw.run_id : null,
    mode: RECORDER_MODES.includes(raw.mode) ? raw.mode : null,
    state,
    exitCode: num(raw.exit_code),
    startedAt: typeof raw.started_at === "string" ? raw.started_at : null,
    finishedAt: typeof raw.finished_at === "string" ? raw.finished_at : null,
    counts: countsOf(raw.counts),
    logTail: typeof raw.log_tail === "string" ? raw.log_tail : "",
    version: num(raw.version),
    problem: typeof raw.problem === "string" && raw.problem ? raw.problem : null,
  };
}

// POST …/recorder {mode} → 202 {run_id, mode, started_at}; 409 unless signed in, or
// while a run is active.
export async function startRecorder(practiceId, mode) {
  const body = await request(
    `${base(practiceId)}/recorder`,
    post({ mode }),
    "Could not start the recorder.",
  );
  return normalizeRecorderRun({ ...body, state: "running" });
}

// GET …/recorder → the run's state; 404 when none is known for the session.
export async function fetchRecorderRun(practiceId) {
  const body = await request(`${base(practiceId)}/recorder`, {}, "Could not read the recorder run.");
  return normalizeRecorderRun(body);
}
